import DownloadIcon from "@mui/icons-material/Download"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    Slider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useMemo, useState } from "react"
import { useMatch, useNavigate, useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import DNOFilterPanelFrame from "../../components/fitlerPanel/dnoFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import UserSuggestionAutocomplete from "../../components/inputs/controlled/userSuggestionAutocomplete"
import DebouncedTextField from "../../components/inputs/debouncedTextField"
import MonthBucketSkeleton from "../../components/loading/skeletons/monthBucketSkeleton"
import { LCT_TILES } from "../../components/submitApplication/single/selectLct/applicationTypeTiles"
import AppPaper from "../../components/surfaces/appPaper"
import MonthHeader from "../../components/surfaces/monthHeader"
import ApplicationTable from "../../components/tables/applicationTable/applicationTable"
import {
    ApplicationStatuses,
    DnoAllResovedStauses,
    DnoApplicationTypes,
    ApplicationDisplayStatuses,
    EscalatedReasons,
} from "../../constants/enums"
import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    POSTCODE,
    SLIDE_IN_TIMEOUT,
    STATUS,
    TECHNOLOGY,
    ADDRESS,
    ESCALATED_REASON,
    INSTALLER_EMAIL,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    UPDATED_SINCE,
} from "../../constants/generalConstants"
import {
    TT_HELP_ALL_NOT_ESCALATED,
    TT_HELP_ESCALATED,
    TT_HELP_DNO_WORKS_REQUIRED,
} from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import useDownloadJson from "../../hooks/useDownloadJson"
import { useGetState } from "../../hooks/useGetState"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import {
    DNO_APPLICATIONS_ESCALATIONS,
    DNO_APPLICATIONS_NOTIFICATIONS,
    DNO_APPLICATIONS_WORKS_REQUIRED,
} from "../../router/routes"
import { User } from "../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import useGetDnoApplicationsMonthly from "../../services/middleware/hooks/dnoInterface/protected/useGetDnoApplicationsMonthly"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

/**
 * Lists all resolved DNO applications with monthly batched loading.
 */
const ListApplications = (): ReactElement => {
    const isEscalations = !!useMatch(DNO_APPLICATIONS_ESCALATIONS)
    const isDnoWorksRequired = !!useMatch(DNO_APPLICATIONS_WORKS_REQUIRED)
    // URL params
    const [searchParams, setSearchParams] = useSearchParams()
    // Statuses that will be injected into the API call that the user did not set —
    // allows us to create new list pages without needing to create searchModes in the API.
    // Using this path (rather than setting them in searchParams)
    // keeps STATUS absent from USER_FILTER_KEYS, so monthly bucketing is preserved.
    const implicitStatuses = useMemo(() => {
        // DNO Works Required tab — scope to that single status
        if (isDnoWorksRequired) {
            return [ApplicationStatuses.dnoWorksRequired]
        }
        return undefined
    }, [isDnoWorksRequired])
    const {
        allData,
        isError,
        isLoadingAny,
        isAutoFilling,
        downloadData,
        monthBuckets,
        isFinalMonth,
        loadMore,
    } = useGetDnoApplicationsMonthly(
        searchParams,
        isEscalations,
        implicitStatuses,
        true
    )
    const MAX_CAPACITY = 100
    const TOT_DEM_MAX_CAPACITY = 100
    // Support hooks
    const { downloadJson } = useDownloadJson()
    const showFilters = useGetState(getShowDnoApplicationFilters)
    const navigate = useNavigate()

    // Filter and sort hooks
    const {
        applicationId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        updatedSince,
        status,
        technology,
        postcode,
        setOrderBy,
        assignedTo,
        address,
        escalatedReason,
        installerEmail,
        genCapacityMin,
        genCapacityMax,
        totDemCapacityMin,
        totDemCapacityMax,
        isUnread,
        setFilterFromSlider,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, true)
    const onSelectUserToFilter = (newValue: User | null): void => {
        setFilterFromTextfield(newValue?.username || "", ASSIGNED_TO)
    }

    const handleSortChange = (_newSortKey: string): void => {
        // Monthly hook sorts by updatedSince inherently; no-op
    }
    const theme = useTheme()
    const [localSliderValue, setLocalSliderValue] = useState<[number, number]>([
        genCapacityMin,
        genCapacityMax,
    ])
    const [localTotDemSliderValue, setLocalTotDemSliderValue] = useState<
        [number, number]
    >([totDemCapacityMin, totDemCapacityMax])
    const onSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalSliderValue(newValue as [number, number])
    }
    const onTotDemSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalTotDemSliderValue(newValue as [number, number])
    }
    const onGenCapacitySliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY])
    }
    const onTotDemSliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_MAX_DEMAND, MAX_MAX_DEMAND])
    }
    const technologyMapping = LCT_TILES.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const techOptions: string[] = LCT_TILES.map((tile) => tile.title)

    const escalationMapping = EscalatedReasons.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const escalationOptions: string[] = EscalatedReasons.map(
        (tile) => tile.title
    )

    let panelHeading: string
    if (isEscalations) panelHeading = "Escalated"
    else if (isDnoWorksRequired) panelHeading = "DNO Works Required"
    else panelHeading = "Responded"

    let panelTooltip: string
    if (isEscalations) panelTooltip = TT_HELP_ESCALATED
    else if (isDnoWorksRequired) panelTooltip = TT_HELP_DNO_WORKS_REQUIRED
    else panelTooltip = TT_HELP_ALL_NOT_ESCALATED

    let panelColour: string
    if (isEscalations) panelColour = theme.palette.primary.main
    else if (isDnoWorksRequired) panelColour = theme.palette.warning.main
    else panelColour = theme.palette.success.main

    let toggleValue: DnoApplicationTypes
    if (isEscalations) toggleValue = DnoApplicationTypes.escalationsIncCx
    else if (isDnoWorksRequired)
        toggleValue = DnoApplicationTypes.dnoWorksRequired
    else toggleValue = DnoApplicationTypes.allApprovedIncCx

    return (
        <Grid container spacing={GRID_SPACING}>
            <DNOFilterPanelFrame
                applicationId={applicationId}
                isValidating={isLoadingAny}
                all={!isEscalations && !isDnoWorksRequired}
                heading={panelHeading}
                applications="Applications"
                tooltip={panelTooltip}
                colour={panelColour}
                isDnoMode
                clearLocalAddress={() => {
                    setFilterFromTextfield("", ADDRESS)
                }}
                clearLocalPostcode={() => {
                    setFilterFromTextfield("", POSTCODE)
                }}
                clearLocalInstallerEmail={() => {
                    setFilterFromTextfield("", INSTALLER_EMAIL)
                }}
                clearLocalGenCapacity={() => {
                    setLocalSliderValue([0, MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY]
                    )
                }}
                clearLocalDemCapacity={() => {
                    setLocalTotDemSliderValue([0, TOT_DEM_MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_MAX_DEMAND, MAX_MAX_DEMAND]
                    )
                }}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <ToggleButtonGroup
                                    color="primary"
                                    fullWidth
                                    value={toggleValue}
                                    exclusive
                                    onChange={(_e, v) => {
                                        let to = DNO_APPLICATIONS_NOTIFICATIONS
                                        if (
                                            v ===
                                            DnoApplicationTypes.escalationsIncCx
                                        )
                                            to = DNO_APPLICATIONS_ESCALATIONS
                                        else if (
                                            v ===
                                            DnoApplicationTypes.dnoWorksRequired
                                        )
                                            to = DNO_APPLICATIONS_WORKS_REQUIRED
                                        searchParams.delete(STATUS)
                                        navigate(
                                            `${to}${
                                                searchParams.toString()
                                                    ? `?${searchParams.toString()}`
                                                    : ""
                                            }`
                                        )
                                    }}
                                >
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.escalationsIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        Escalations
                                    </ToggleButton>
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.allApprovedIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        All
                                    </ToggleButton>
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.dnoWorksRequired
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        DNO Works Required
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Application ID"
                                    value={applicationId}
                                    placeholder="Full application ID required..."
                                    onDebouncedChange={(value) =>
                                        setFilterFromTextfield(
                                            value,
                                            APPLICATION_ID_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_FROM_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_TO_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Last Updated"
                                    dateValue={updatedSince}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(v, UPDATED_SINCE)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={Object.values(
                                        DnoAllResovedStauses
                                    )}
                                    label="Status"
                                    setValue={(
                                        e_: SyntheticEvent<Element, Event>,
                                        nv: string | number | null
                                    ) =>
                                        setFilterFromTextfield(
                                            nv as string,
                                            STATUS
                                        )
                                    }
                                    getOptionLabel={(option) =>
                                        String(option) ===
                                        DnoAllResovedStauses.dnoFurtherInfoRequired
                                            ? ApplicationDisplayStatuses.installerInformationRequired
                                            : String(option)
                                    }
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={technology}
                                    options={techOptions}
                                    label="Technology"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            technologyMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            TECHNOLOGY
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={escalatedReason}
                                    options={escalationOptions}
                                    label="Escalated Reason"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            escalationMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            ESCALATED_REASON
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Installer Email"
                                    value={installerEmail || ""}
                                    placeholder="Enter Installers email..."
                                    onDebouncedChange={(value) =>
                                        setFilterFromTextfield(
                                            value,
                                            INSTALLER_EMAIL
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Postcode"
                                    value={postcode || ""}
                                    placeholder="Enter postcode..."
                                    transform={(value) => value.toUpperCase()}
                                    onDebouncedChange={(value) =>
                                        setFilterFromTextfield(value, POSTCODE)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <DebouncedTextField
                                    label="Address"
                                    value={address || ""}
                                    placeholder="Enter address..."
                                    onDebouncedChange={(value) =>
                                        setFilterFromTextfield(value, ADDRESS)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <UserSuggestionAutocomplete
                                    onSelectedUserChange={onSelectUserToFilter}
                                    userId={assignedTo}
                                    filter
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Generation Capacity
                        : ${localSliderValue[0]} - ${localSliderValue[1]}${
                            localSliderValue[1] === MAX_CAPACITY ? "+" : ""
                        } kW`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localSliderValue}
                                        max={MAX_CAPACITY}
                                        onChange={onSlide}
                                        onChangeCommitted={
                                            onGenCapacitySliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index: number) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: MAX_CAPACITY,
                                                label: `${MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Total demand
                        : ${localTotDemSliderValue[0]} - ${
                            localTotDemSliderValue[1]
                        }${
                            localTotDemSliderValue[1] === TOT_DEM_MAX_CAPACITY
                                ? "+"
                                : ""
                        } A`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localTotDemSliderValue}
                                        max={TOT_DEM_MAX_CAPACITY}
                                        onChange={onTotDemSlide}
                                        onChangeCommitted={
                                            onTotDemSliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index: number) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: TOT_DEM_MAX_CAPACITY,
                                                label: `${TOT_DEM_MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isUnread}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "isUnread"
                                                )
                                            }}
                                        />
                                    }
                                    label="Any Unread Comments?"
                                />
                            </Grid>
                            <Grid
                                xl={6}
                                md={6}
                                sm={12}
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    pr: 2,
                                }}
                            >
                                <AppButton
                                    variant="contained"
                                    endIcon={<DownloadIcon />}
                                    disabled={
                                        isLoadingAny || isError || !downloadData
                                    }
                                    onClick={() =>
                                        downloadData &&
                                        downloadJson(downloadData, "results")
                                    }
                                >
                                    Download
                                </AppButton>
                            </Grid>
                        </Grid>
                    </Collapse>

                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            {monthBuckets.map((bucket) => (
                                <Collapse
                                    key={bucket.key}
                                    in
                                    appear
                                    timeout={300}
                                >
                                    <Box>
                                        <MonthHeader
                                            label={bucket.label}
                                            recordCount={bucket.data.length}
                                            isLoading={bucket.isLoading}
                                            monthKey={bucket.key}
                                        />
                                        <ApplicationTable
                                            searchResults={bucket.data}
                                            orderBy={orderBy}
                                            setOrderBy={setOrderBy}
                                            orderDirection={orderDirection}
                                            isLoading={
                                                bucket.data.length === 0 &&
                                                !bucket.isComplete
                                            }
                                            isEscalations={false}
                                            onSortChange={handleSortChange}
                                        />
                                    </Box>
                                </Collapse>
                            ))}
                            {(isAutoFilling || isLoadingAny) && !isError && (
                                <MonthBucketSkeleton />
                            )}
                            {allData && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {!isFinalMonth && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isLoadingAny}
                                            loadingPosition="end"
                                            onClick={loadMore}
                                            sx={{
                                                width: "20%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default ListApplications
